import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Activity,
  AlertTriangle,
  RefreshCw,
  Radio,
  Eye,
  ShieldCheck,
  Settings,
} from 'lucide-react';
import { getActiveWatches } from '../../services/eventWatcherApi';
import WatchCard from './WatchCard';
import EventHistory from './EventHistory';

// Auto-refresh cadence (ms). The watcher collects metrics every 60s server-side,
// so polling at 30s keeps the UI fresh without burning bandwidth.
const POLL_INTERVAL_MS = 30_000;

// Convert a UTC ISO string into Texas Central Time. DST handled by Intl.
const fmtCentral = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

const severityRank = { Critical: 4, High: 3, Medium: 2, Low: 1, Info: 0 };

export default function EventWatcher() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const resp = await getActiveWatches();
      setData(resp);
      setError(null);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message || 'Failed to load active watches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Sort: Monitoring first (by most recent), then Discovered.
  const sorted = useMemo(() => {
    if (!data?.watches) return [];
    return [...data.watches].sort((a, b) => {
      if (a.status !== b.status) {
        if (a.status === 'Monitoring') return -1;
        if (b.status === 'Monitoring') return 1;
      }
      return new Date(b.eventDateTime || 0) - new Date(a.eventDateTime || 0);
    });
  }, [data]);

  const summary = useMemo(() => {
    const s = { monitoring: 0, discovered: 0, totalAlerts: 0, peakConns: 0 };
    for (const w of sorted) {
      if (w.status === 'Monitoring') s.monitoring += 1;
      if (w.status === 'Discovered') s.discovered += 1;
      s.totalAlerts += w.totalAlerts || 0;
      s.peakConns = Math.max(s.peakConns, w.peakConnections || 0);
    }
    return s;
  }, [sorted]);

  if (selectedId) {
    return (
      <EventHistory
        monitoredEventId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Radio className="text-blue-600 dark:text-blue-400" size={24} />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              Event Watcher
            </h1>
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
              <ShieldCheck size={12} /> read-only
            </span>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Live auction observability. Watcher NEVER writes to tenant systems — only reads & suggests.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Updated {fmtCentral(lastRefresh.toISOString())}
            </span>
          )}
          <button
            onClick={refresh}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            disabled={loading}
          >
            <RefreshCw className={loading ? 'animate-spin' : ''} size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryTile icon={Eye} label="Monitoring" value={summary.monitoring} accent="blue" />
        <SummaryTile icon={Activity} label="Discovered" value={summary.discovered} accent="slate" />
        <SummaryTile icon={AlertTriangle} label="Total Alerts" value={summary.totalAlerts} accent="amber" />
        <SummaryTile icon={Settings} label="Peak Connections" value={summary.peakConns || '—'} accent="emerald" />
      </div>

      {/* Error / empty */}
      {error && (
        <div className="mb-4 p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          <strong>Failed to load:</strong> {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-12 text-slate-500 dark:text-slate-400">
          Loading active watches…
        </div>
      )}

      {!loading && sorted.length === 0 && !error && (
        <div className="text-center py-16 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800/50">
          <Radio className="mx-auto mb-3 text-slate-400" size={32} />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            No events are currently being watched.
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            The watcher will start monitoring automatically within 30 minutes of a scheduled event.
          </p>
        </div>
      )}

      {/* Cards */}
      <div className="space-y-4">
        {sorted.map((w) => (
          <WatchCard
            key={w.monitoredEventId}
            watch={w}
            onOpenHistory={() => setSelectedId(w.monitoredEventId)}
          />
        ))}
      </div>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, accent }) {
  const accents = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    slate: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
  };
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
      <div className={`rounded-lg p-2 ${accents[accent]}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-xl font-bold text-slate-900 dark:text-slate-100">{value}</p>
      </div>
    </div>
  );
}

export { severityRank };
